import { Button, DialogActions, DialogContent, FormControl } from "@mui/material";
import { useFormik } from "formik";

import AppTextArea from "@/common/components/Form/Input/AppTextArea";
import MileageInput from "@/common/components/Form/Input/MileageInput";
import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import { ValidationHelper } from "@/common/validation";
import { VehicleDto, VehicleMileageDto, VehicleMileageInputDto } from "@/core/api/generated/v1";

import AppModal, { AppModalProps } from "../AppModal";

interface Props extends Omit<AppModalProps, "children"> {
  vehicleId?: string;
  vehicle?: VehicleDto;
  mileage?: VehicleMileageDto;
  onConfirm: (mileage: VehicleMileageInputDto) => Promise<void>;
  onMileageChanged: () => void;
}

export default function BaseEditMileageModal({
  vehicle,
  vehicleId,
  mileage,
  onClose,
  onMileageChanged,
  onConfirm,
  ...otherProps
}: Props) {
  const formik = useFormik<VehicleMileageInputDto>({
    enableReinitialize: true,
    initialValues: {
      mileage: mileage?.mileage || undefined,
    },
    onSubmit: async (values, { setSubmitting, setFieldError, setStatus }) => {
      setSubmitting(true);
      try {
        await onConfirm?.(values);
        onMileageChanged();
        onClose?.();
      } catch (err: any) {
        ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
        setStatus({ success: false });
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { values, errors, touched, handleBlur, handleSubmit, setFieldValue, isSubmitting } = formik;

  return (
    <AppModal fullWidth maxWidth='md' onClose={onClose} {...otherProps}>
      <AppModalTitle title={"Update mileage"} withClose onClose={onClose} />

      <DialogContent>
        {/* Mileage */}
        <MileageInput
          error={Boolean(touched.mileage && errors.mileage)}
          helperText={touched.mileage && errors.mileage}
          vehicle={vehicle}
          vehicleId={vehicleId}
          disabled={!vehicle && !vehicleId}
          fullWidth
          name='mileage'
          onBlur={handleBlur}
          onChange={(newValue) => {
            setFieldValue("mileage", newValue?.mileage);
            setFieldValue("attachments", newValue?.attachments);
          }}
          value={values}
        />
        {/* Notes */}
        <FormControl fullWidth margin='dense'>
          <AppTextArea
            mode='notes'
            name='notes'
            error={Boolean(touched.notes && errors.notes)}
            helperText={touched.notes && errors.notes}
            rows={4}
            value={values.notes}
            onBlur={handleBlur}
            onChange={(e) => {
              setFieldValue("notes", e.target.value);
            }}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ mb: 1 }}>
        <Button variant='outlined' color='text' sx={{ minWidth: 200 }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          loading={isSubmitting}
          sx={{ minWidth: 200 }}
          onClick={() => {
            handleSubmit();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </AppModal>
  );
}
